import { useEffect, useMemo, useState } from "react";
import { ClinicInfo } from "./ClinicInfo";
import { ClinicUsers } from "./ClinicUsers";
import { errorMessages } from "../../../../constants/errorMessages";
import {
  useLazyGetClinicLicensesQuery,
  useGetClinicUserDetailsQuery,
  useLazyAssignProviderQuery,
  useLazyGetProviderDataQuery,
  useLazyUnassignProviderQuery,
} from "../../../../services/rtkQueryServices/clinicService";
import GettingStartedModal from "../../GettingStartedModal/GettingStartedModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { routePaths } from "../../../../constants/routePaths";
import { useAuth } from "../../../../context/AuthProvider";
import { Roles } from "../../../../constants/data/Roles";
import { ReactComponent as LeftArrow } from "../../../../assets/svg/LeftArrow.svg";
import { type ClinicUserDetails } from "../../../../models/response/clinicUserDetails";
import { useLazyGetAddressBookLastUpdatedStatusQuery } from "../../../../services/rtkQueryServices/authenticationService";
import { UserTabs } from "../../../shared/Tabs/Tabs";
import ReportingGrid from "../../../ReportingGrid/ReportingGrid";

export function ClinicUserManagement() {
  const [refetchDetails, setRefetchDetails] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isBpSiteIdVerified, setIsBpSiteIdVerified] = useState<boolean | null>(
    null
  );
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<
    boolean | null
  >(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [navigatedFromRegistration, setNavigatedFromRegistration] =
    useState(false);
  const [isClinicLogoExists, setIsClinicLogoExists] = useState<any>(null);
  const [isLicencesAssigned, setIsLicencesAssigned] = useState<any>(null);
  const [addressBookLastUpdateStatus, setAddressBookLastUpdateStatus] =
    useState<string>("");
  const { applicationUserId } = useParams();
  const [tabValue, setTabValue] = useState<number>(-1);
  const [isGetLicensesFailed, setGetLicensesFailed] = useState<boolean>(false);

  const auth = useAuth();
  let role = auth?.authClaims?.role;

  useEffect(() => {
    // Check if user navigated from registration or login page
    const lastLocation = location.state?.from;
    var currentLocation = location.pathname.split("/")[1];
    if (
      lastLocation === routePaths.register ||
      (lastLocation === routePaths.login && !(currentLocation === ""))
    ) {
      setNavigatedFromRegistration(true);
      // Clear the state after setting the variable
      navigate(location.pathname); // Reset location state
    } else if (currentLocation === "reporting") {
      setTabValue(1);
    } else if (
      currentLocation === "clinicDetails" ||
      (role === Roles.clinicAdmin &&
        (currentLocation === "clinicManagement" || currentLocation === ""))
    ) {
      setTabValue(0);
    } else {
      setNavigatedFromRegistration(false);
    }
  }, []);

  let userId =
    role === Roles.clinicAdmin
      ? auth?.authClaims?.applicationUserId
      : applicationUserId;
  const {
    isFetching,
    isSuccess,
    isError,
    data: clinicUserDetails,
    refetch,
  } = useGetClinicUserDetailsQuery({ applicationUserId: userId });
  const [AssignProvider] = useLazyAssignProviderQuery();
  const [UnassignProvider] = useLazyUnassignProviderQuery();
  const [getAddressBookLastUpdatedStatus] =
    useLazyGetAddressBookLastUpdatedStatusQuery();
  const [setProvidersInfo, providersInfo] = useLazyGetProviderDataQuery({});
  const [getClinicLicenses] = useLazyGetClinicLicensesQuery();
  const [rowData, setRowData] = useState<any[]>([]);

  const clinicUserData: ClinicUserDetails = useMemo(() => {
    if (isSuccess && !isFetching) {
      setIsBpSiteIdVerified(clinicUserDetails?.data?.bpSiteId ? true : false);
      setIsSubscriptionActive(clinicUserDetails?.data?.isActive);
      if (!clinicUserDetails?.data?.clinicLogoUrl) {
        setIsClinicLogoExists(false);
      } else {
        setIsClinicLogoExists(true);
      }
      setAddressBookLastUpdateStatus(
        clinicUserDetails?.data?.addressBookLastUpdateStatus
      );
      return clinicUserDetails?.data;
    }
  }, [clinicUserDetails]);

  function getClinicLicencesInformation() {
    getClinicLicenses({ applicationUserId: userId }).then((res) => {
      if (res?.isSuccess && !res?.isLoading) {
        setRowData(res?.data?.data?.clinicLicenses);
      } else if (res?.isError || !res?.data?.data) {
        setGetLicensesFailed(true);
      }
    });
  }

  useEffect(() => {
    if (tabValue == 0) {
      getClinicLicencesInformation();
      setProvidersInfo({ applicationUserId: userId });
    }
  }, [tabValue]);

  useEffect(() => {
    let interval: any = null;
    const checkStatus = () => {
      if (addressBookLastUpdateStatus?.trim()?.toUpperCase() === "INPROGRESS") {
        interval = setInterval(() => {
          getAddressBookLastUpdatedStatus({
            applicationUserId: applicationUserId,
            bpSiteId: clinicUserData.bpSiteId,
          }).then((res) => {
            if (res.isSuccess && !res.isLoading) {
              setAddressBookLastUpdateStatus(res?.data?.data);
            }
          });
        }, 10000);
      }
    };
    checkStatus();
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [addressBookLastUpdateStatus]);

  useEffect(() => {
    isBpSiteIdVerified &&
      isSubscriptionActive &&
      tabValue === 0 &&
      setProvidersInfo({ applicationUserId: userId });
  }, [isBpSiteIdVerified, isSubscriptionActive]);

  const selectedProviders = useMemo(() => {
    if (rowData && rowData?.length) {
      return rowData
        .filter((row: any) => row.providerId.length > 0)
        .map((row: any) => row.providerId);
    }
  }, [rowData]);

  useEffect(() => {
    if (selectedProviders?.length === 0) {
      setIsLicencesAssigned(false);
    } else if (selectedProviders) {
      setIsLicencesAssigned(true);
    }
  }, [selectedProviders]);

  const providersData = useMemo(() => {
    if (providersInfo.isSuccess && !providersInfo.isFetching) {
      let res = providersInfo?.data?.data.map((item: any) => {
        return {
          value: item,
          displayValue: item.gpName + " - " + item.providerNo,
        };
      });
      if (selectedProviders) {
        return res?.filter(
          (item: any) => !selectedProviders.includes(item.value.providerNo)
        );
      }
    }
    return [];
  }, [selectedProviders, providersInfo]);

  useEffect(() => {
    if (isError || isGetLicensesFailed) {
      setErrorMessage(errorMessages.errorInfo);
    }
  }, [isError, isGetLicensesFailed]);

  useEffect(() => {
    if (refetchDetails) {
      refetch();
      setRefetchDetails(false);
    }
  }, [refetchDetails]);

  useEffect(() => {
    if (isError) {
      setErrorMessage(errorMessages.errorInfo);
    }
  }, [isError]);
  useEffect(() => {
    if (
      navigatedFromRegistration &&
      (isClinicLogoExists === false || isLicencesAssigned === false)
    ) {
      setOpenModal(true);
    } else if (
      navigatedFromRegistration &&
      isClinicLogoExists !== null &&
      isLicencesAssigned !== null
    ) {
      setNavigatedFromRegistration(false);
    }
  }, [isClinicLogoExists, isLicencesAssigned]);
  const AssignLicenceToProvider = (licenceKey: any, gpUserId: any) => {
    AssignProvider({
      licenseKey: licenceKey,
      applicationUserId: userId,
      gpUserId: gpUserId,
    })
      .then((res) => {
        if (res.isSuccess) {
          setProvidersInfo({ applicationUserId: userId });
          refetch();
          getClinicLicencesInformation();
        } else if (res.isError) {
          setErrorMessage(errorMessages.errorInfo);
        }
      })
      .catch((error) => {
        setErrorMessage(errorMessages.errorInfo);
      });
  };
  const unAssignProviderLicence = (licenseKey: any) => {
    UnassignProvider({ licenseKey: licenseKey, applicationUserId: userId })
      .then((res) => {
        if (res.isSuccess) {
          setProvidersInfo({ applicationUserId: userId });
          refetch();
          getClinicLicencesInformation();
        } else if (res.isError) {
          setErrorMessage(errorMessages.errorInfo);
        }
      })
      .catch((error) => {
        setErrorMessage(errorMessages.errorInfo);
      });
  };
  return (
    <>
      <div className="d-flex flex-column gap-3 px-3 h-100 overflow-auto">
        {(role === Roles.superUser || role === Roles.supportUser) && (
          <div className="navigation-button d-flex align-items-center">
            <span
              onClick={() => {
                navigate(routePaths.clinicManagement);
              }}
            >
              <LeftArrow />
              Back to All Clinics
            </span>
          </div>
        )}
        {(role === Roles.clinicAdmin || role === Roles.superUser) && (
          <UserTabs
            value={tabValue}
            items={["Clinic Details", "Patient Billing Report"]}
            handleChange={(value) => {
              if (value === 1) {
                navigate(`/reporting/${userId}`);
              } else {
                navigate(`/clinicDetails/${userId}`);
              }
              setTabValue(value);
            }}
          />
        )}
        {tabValue === 0 && (
          <>
            <div>
              <ClinicInfo
                refetchClinicDetails={() => {
                  setRefetchDetails(true);
                }}
                error={errorMessage}
                isBpSiteIdVerified={isBpSiteIdVerified}
                isSubscriptionActive={isSubscriptionActive}
                clinicUserData={clinicUserData}
                addressBookLastUpdateStatus={addressBookLastUpdateStatus}
              />
            </div>
            <div>
              <ClinicUsers
                refetchClinicDetails={() => {
                  setRefetchDetails(true);
                  getClinicLicencesInformation();
                }}
                rowData={rowData}
                isError={() => {
                  setErrorMessage(errorMessages.errorInfo);
                }}
                isBpSiteIdVerified={isBpSiteIdVerified}
                isSubscriptionActive={isSubscriptionActive}
                isNoLicencesAssigned={(value: boolean) => {
                  setIsLicencesAssigned(!value);
                }}
                assignLicence={(licenseKey: any, gpUserId: any) => {
                  AssignLicenceToProvider(licenseKey, gpUserId);
                }}
                unassignLicence={(licenseKey: any) => {
                  unAssignProviderLicence(licenseKey);
                }}
                providersData={providersData}
              />
            </div>
          </>
        )}
        {tabValue === 1 && (
          <>
            <ReportingGrid
              userId={userId}
              isSubscriptionActive={isSubscriptionActive}
              isBpSiteIdVerified={isBpSiteIdVerified}
              setTabValue={setTabValue}
            />
          </>
        )}
      </div>
      <GettingStartedModal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setNavigatedFromRegistration(false);
        }}
      />
    </>
  );
}
